/*
  Creator videos: card carousel -> shared modal.

  The carousel is the theme's <carousel-slider> (main.js) and the modal is the theme's
  <modal-dialog> (main.js) - focus trap, Escape, backdrop click, body scroll lock and
  pauseAllMedia() all live there. This file only clones a card's <template>s into the
  modal, starts playback, and tears the media down again on close.
*/
if (!customElements.get('creator-videos')) {
  class CreatorVideos extends HTMLElement {
    static normalizePermalink(value) {
      if (!value) return null;

      let url;
      try {
        url = new URL(value);
      } catch {
        return null;
      }

      if (url.protocol !== 'https:') return null;
      if (!CreatorVideos.HOSTS.includes(url.hostname)) return null;
      if (url.username || url.password || url.port) return null;

      const match = url.pathname.match(CreatorVideos.PATH);
      if (!match) return null;

      return `https://www.instagram.com/${match[1]}/${match[2]}/`;
    }

    static safeHref(value) {
      if (!value) return null;

      let url;
      try {
        url = new URL(value);
      } catch {
        return null;
      }

      return url.protocol === 'https:' ? url.href : null;
    }

    static formatTime(seconds) {
      if (!Number.isFinite(seconds) || seconds < 0) return CreatorVideos.ZERO_TIME;

      const total = Math.floor(seconds);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor(total / 60) % 60;
      const secs = total % 60;
      const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);

      return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(secs).padStart(2, '0')}`;
    }

    static loadEmbedScript() {
      if (window.instgrm && window.instgrm.Embeds) return Promise.resolve();
      if (CreatorVideos.embedPromise) return CreatorVideos.embedPromise;

      CreatorVideos.embedPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${CreatorVideos.EMBED_SRC}"]`);
        const script = existing || document.createElement('script');

        const fail = () => {
          // Reset so a later open can retry, but only drop a script we injected ourselves.
          CreatorVideos.embedPromise = null;
          if (!existing) script.remove();
          reject(new Error('instagram-embed-unavailable'));
        };

        script.addEventListener('load', () => {
          if (window.instgrm && window.instgrm.Embeds) {
            resolve();
          } else {
            fail();
          }
        }, { once: true });

        script.addEventListener('error', fail, { once: true });

        if (!existing) {
          script.src = CreatorVideos.EMBED_SRC;
          script.async = true;
          document.head.appendChild(script);
        }
      });

      return CreatorVideos.embedPromise;
    }

    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();

      this.modal = document.getElementById(this.dataset.cvModalId);
      if (!this.modal) return;

      this.modalTitle = this.modal.querySelector('[data-cv-modal-title]');
      this.modalCreator = this.modal.querySelector('[data-cv-modal-creator]');
      this.modalPostLink = this.modal.querySelector('[data-cv-post-link]');
      this.modalMedia = this.modal.querySelector('[data-cv-modal-media]');
      this.modalPanel = this.modal.querySelector('[data-cv-modal-panel]');
      this.muteToggle = this.modal.querySelector('[data-cv-mute-toggle]');

      const { signal } = this.controller;

      this.addEventListener('click', this.handleClick.bind(this), { signal });
      this.addEventListener('contextmenu', this.handleContextMenu.bind(this), { signal });

      this.modalObserver = new MutationObserver(() => {
        if (!this.modal.hasAttribute('open')) this.handleModalClose();
      });
      this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
    }

    disconnectedCallback() {
      this.closeModal();
      clearTimeout(this.sheetTimeout);
      this.sheetTimeout = null;
      this.modalObserver?.disconnect();
      this.modalObserver = null;
      this.controller?.abort();
      this.controller = null;
    }

    handleClick(evt) {
      const playToggle = evt.target.closest('[data-cv-play-toggle]');
      if (playToggle && this.modal?.contains(playToggle)) {
        evt.preventDefault();
        this.togglePlayback();
        return;
      }

      const muteToggle = evt.target.closest('[data-cv-mute-toggle]');
      if (muteToggle && this.modal?.contains(muteToggle)) {
        evt.preventDefault();
        this.toggleMute();
        return;
      }

      const toggle = evt.target.closest('[data-cv-sheet-toggle]');
      if (toggle && this.modalPanel && this.modal?.contains(toggle)) {
        evt.preventDefault();
        this.toggleSheet(toggle);
        return;
      }

      const media = evt.target.closest('[data-cv-modal-media]');
      if (media && this.modal?.contains(media)) {
        if (this.modalPanel?.hasAttribute('data-cv-anim')) return;
        if (this.modal.dataset.cvMediaType !== 'shopify') return;

        evt.preventDefault();

        if (this.modalPanel?.hasAttribute('data-cv-expanded')) {
          this.toggleSheet(this.modalPanel.querySelector('[data-cv-sheet-toggle]'));
          return;
        }

        this.togglePlayback();
        return;
      }

      const trigger = evt.target.closest('[data-cv-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      // Let the browser handle modified clicks: the trigger can be a real <a href>.
      if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;

      const item = trigger.closest('.slider__item');
      if (!item) return;

      evt.preventDefault();
      this.openCard(item, trigger);
    }

    handleContextMenu(evt) {
      const media = evt.target.closest('[data-cv-modal-media]');
      if (!media || !this.modal?.contains(media)) return;

      if (this.modal.dataset.cvMediaType !== 'shopify') return;

      evt.preventDefault();
    }

    toggleSheet(toggle) {
      const panel = this.modalPanel;
      if (!panel) return;

      clearTimeout(this.sheetTimeout);

      const expanding = !panel.hasAttribute('data-cv-expanded');
      toggle?.setAttribute('aria-expanded', String(expanding));

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        panel.removeAttribute('data-cv-anim');
        panel.toggleAttribute('data-cv-expanded', expanding);
        return;
      }

      panel.setAttribute('data-cv-anim', '');
      this.sheetTimeout = setTimeout(() => {
        panel.toggleAttribute('data-cv-expanded', expanding);
        panel.setAttribute('data-cv-anim', '');
        requestAnimationFrame(() => requestAnimationFrame(() => panel.removeAttribute('data-cv-anim')));
      }, CreatorVideos.SHEET_ANIM);
    }

    setPaused(paused) {
      if (!this.modal) return;
      this.modal.toggleAttribute('data-cv-paused', paused);

      const label = paused ? this.dataset.cvPlayText : this.dataset.cvPauseText;
      if (!label) return;

      this.modal.querySelector('[data-cv-play-toggle]')?.setAttribute('aria-label', label);
    }

    setMuted(muted) {
      if (!this.modal) return;
      this.modal.toggleAttribute('data-cv-muted', muted);

      const label = muted ? this.dataset.cvUnmuteText : this.dataset.cvMuteText;
      if (label) this.muteToggle?.setAttribute('aria-label', label);
    }

    togglePlayback() {
      const video = this.modalMedia?.querySelector('video');
      if (!video) return;

      if (video.paused || video.ended) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }

    toggleMute() {
      const video = this.modalMedia?.querySelector('video');
      if (!video) return;

      video.muted = !video.muted;
    }

    seekTo(value) {
      const video = this.modalMedia?.querySelector('video');
      if (!video) return;

      const duration = video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      const ratio = Math.min(Math.max(Number(value) / 100, 0), 1);
      if (!Number.isFinite(ratio)) return;

      video.currentTime = ratio * duration;
      this.modal?.style.setProperty('--cv-progress', String(ratio));
    }

    resetControls() {
      this.muteToggle?.setAttribute('hidden', '');
      this.modal?.removeAttribute('data-cv-muted');

      if (this.dataset.cvMuteText) this.muteToggle?.setAttribute('aria-label', this.dataset.cvMuteText);
    }

    openCard(item, trigger) {
      const active = CreatorVideos.activeInstance;
      if (active && active !== this) active.closeModal();

      // Drops any observer/timeout left by the previous open and invalidates its token, so
      // late async callbacks from that open stop writing to the DOM.
      this.resetEmbed();
      const token = Symbol('cv-open');
      this.openToken = token;

      const title = item.dataset.cvTitle || '';
      const mediaTemplate = item.querySelector('template[data-cv-media]');
      const panelTemplate = item.querySelector('template[data-cv-panel]');

      if (this.modalTitle) this.modalTitle.textContent = title;
      if (this.modalCreator) this.modalCreator.textContent = item.dataset.cvCreator || '';

      if (this.modalPostLink) {
        const postUrl = CreatorVideos.safeHref(item.dataset.cvPostUrl);
        if (postUrl) {
          this.modalPostLink.href = postUrl;
        } else {
          this.modalPostLink.removeAttribute('href');
        }
      }
      if (this.modalPanel) {
        clearTimeout(this.sheetTimeout);
        this.sheetTimeout = null;
        this.modalPanel.removeAttribute('data-cv-expanded');
        this.modalPanel.removeAttribute('data-cv-anim');
        this.modalPanel.replaceChildren(
          ...(panelTemplate ? [panelTemplate.content.cloneNode(true)] : [])
        );
      }
      this.modal.toggleAttribute('data-cv-has-panel', Boolean(panelTemplate));
      this.modal.setAttribute('data-cv-media-type', mediaTemplate?.dataset.cvMediaType || '');
      this.modal.toggleAttribute('data-cv-stories', (mediaTemplate?.dataset.cvMediaType || '') !== 'instagram');
      this.modal.removeAttribute('data-cv-paused');
      this.modal.style.setProperty('--cv-progress', '0');

      this.resetControls();
      // The play toggle is driven by [data-cv-media-type] in CSS; only the mute pill uses [hidden].
      if (mediaTemplate?.dataset.cvMediaType === 'shopify') this.muteToggle?.removeAttribute('hidden');

      // Modal.open() calls window.pauseAllMedia(), so the media has to be cloned in after
      // the modal is open - otherwise the video is paused the instant it is injected.
      this.modal.open(trigger);
      CreatorVideos.activeInstance = this;

      if (!this.modalMedia) return;
      this.modalMedia.replaceChildren(
        ...(mediaTemplate ? [mediaTemplate.content.cloneNode(true)] : [])
      );

      const clonedVideo = this.modalMedia.querySelector('video');
      if (clonedVideo) clonedVideo.controls = false;

      window.trapFocus?.(this.modal, this.modal.querySelector('.modal__window'));

      if (mediaTemplate?.dataset.cvMediaType === 'instagram') {
        this.renderInstagram(mediaTemplate.dataset.cvInstagramUrl, title, token);
        return;
      }

      requestAnimationFrame(() => {
        if (this.openToken !== token) return;
        const video = this.modalMedia.querySelector('video');
        if (!video) return;

        if (mediaTemplate?.dataset.cvMediaType === 'shopify') {
          this.trackProgress(video, token);

          const signal = this.mediaController.signal;
          const sync = () => this.setPaused(video.paused || video.ended);

          ['play', 'playing', 'pause', 'ended'].forEach((name) => {
            video.addEventListener(name, sync, { signal });
          });

          video.addEventListener('volumechange', () => this.setMuted(video.muted), { signal });
          this.setMuted(video.muted);
        }

        // Only pose paused once playback is known to have failed: seeding it up front races
        // the queued 'play' event and flashes the play icon through its 0.2s fade.
        video.play().catch(() => {
          if (this.openToken === token) this.setPaused(true);
        });
      });
    }

    trackProgress(video, token) {
      this.mediaController?.abort();
      this.mediaController = new AbortController();

      const update = () => {
        if (this.openToken !== token) return;

        const duration = video.duration;
        if (!Number.isFinite(duration) || duration <= 0) return;

        const ratio = Math.min(Math.max(video.currentTime / duration, 0), 1);
        this.modal.style.setProperty('--cv-progress', String(ratio));
      };

      video.addEventListener('timeupdate', update, { signal: this.mediaController.signal });
      video.addEventListener('loadedmetadata', update, { signal: this.mediaController.signal });
    }

    renderInstagram(url, label, token) {
      const mount = this.modalMedia.querySelector('[data-cv-ig-mount]');
      if (!mount) return;

      const permalink = CreatorVideos.normalizePermalink(url);
      if (!permalink) {
        this.failEmbed(token);
        return;
      }

      const blockquote = document.createElement('blockquote');
      blockquote.className = 'instagram-media';
      blockquote.setAttribute('data-instgrm-permalink', permalink);
      blockquote.setAttribute('data-instgrm-version', '14');

      // Stays visible as the whole content if Instagram's script never runs.
      const link = document.createElement('a');
      link.href = permalink;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = label || permalink;
      blockquote.appendChild(link);

      mount.replaceChildren(blockquote);
      this.modalMedia.setAttribute('aria-busy', 'true');
      this.setStatus(this.dataset.cvIgLoadingText || CreatorVideos.LOADING_TEXT);

      this.embedTimeout = setTimeout(() => this.failEmbed(token), CreatorVideos.TIMEOUT);

      this.embedObserver = new MutationObserver(() => {
        if (this.openToken !== token) return;

        const iframe = mount.querySelector('iframe:not([data-cv-ig-seen])');
        if (!iframe) return;

        iframe.setAttribute('data-cv-ig-seen', '');
        if (label) iframe.title = label;
        iframe.addEventListener('load', () => {
          if (this.openToken !== token) return;
          this.resetEmbed();
          this.modalMedia.setAttribute('aria-busy', 'false');
          this.setStatus('');
        }, { once: true });
      });
      this.embedObserver.observe(mount, { childList: true, subtree: true });

      CreatorVideos.loadEmbedScript()
        .then(() => {
          if (this.openToken !== token || !this.modal.hasAttribute('open')) return;
          window.instgrm.Embeds.process();
        })
        .catch(() => this.failEmbed(token));
    }

    failEmbed(token) {
      if (this.openToken !== token) return;

      if (this.modalMedia?.querySelector('iframe')) {
        this.resetEmbed();
        this.modalMedia.setAttribute('aria-busy', 'false');
        this.setStatus('');
        return;
      }

      this.resetEmbed();
      this.modalMedia?.setAttribute('aria-busy', 'false');
      this.setStatus(this.dataset.cvIgErrorText || CreatorVideos.ERROR_TEXT);
    }

    setStatus(message) {
      if (!this.modalMedia) return;

      let status = this.modalMedia.querySelector('[data-cv-ig-status]');
      if (!status) {
        if (!message) return;
        status = document.createElement('p');
        status.className = 'creator-videos__ig-status';
        status.setAttribute('data-cv-ig-status', '');
        status.setAttribute('role', 'status');
        this.modalMedia.appendChild(status);
      }

      status.textContent = message || '';
    }

    /**
     * Stops watching for the Instagram embed, without clearing any message already shown.
     */
    resetEmbed() {
      this.embedObserver?.disconnect();
      this.embedObserver = null;
      clearTimeout(this.embedTimeout);
      this.embedTimeout = null;
    }

    /**
     * Closes the modal (teardown runs from the 'open' attribute observer).
     */
    closeModal() {
      if (this.modal?.hasAttribute('open')) this.modal.close();
    }

    handleModalClose() {
      if (CreatorVideos.activeInstance === this) CreatorVideos.activeInstance = null;

      this.openToken = null;
      this.resetEmbed();
      this.teardownMedia();
      this.resetControls();

      clearTimeout(this.sheetTimeout);
      this.sheetTimeout = null;
      this.modalPanel?.removeAttribute('data-cv-expanded');
      this.modalPanel?.removeAttribute('data-cv-anim');
      this.modalPanel?.replaceChildren();
      this.modal.removeAttribute('data-cv-has-panel');
      this.modal.removeAttribute('data-cv-media-type');
      this.modal.removeAttribute('data-cv-stories');
      this.modal.removeAttribute('data-cv-paused');
      this.modal.style.setProperty('--cv-progress', '0');
      if (this.modalTitle) this.modalTitle.textContent = '';
      if (this.modalCreator) this.modalCreator.textContent = '';
      this.modalPostLink?.removeAttribute('href');
    }

    teardownMedia() {
      this.mediaController?.abort();
      this.mediaController = null;

      if (!this.modalMedia) return;

      // Detaching a <video> does not reliably stop playback or buffering, so drop every
      // source and re-run the resource selection algorithm before removing it.
      this.modalMedia.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        video.load();
      });

      this.modalMedia.querySelectorAll('video-component').forEach((videoComponent) => {
        try {
          videoComponent.player?.destroy?.();
        } catch {
          // Player already gone.
        }
      });

      // YouTube/Vimeo/Instagram iframes only stop once their document is destroyed.
      this.modalMedia.replaceChildren();
      this.modalMedia.removeAttribute('aria-busy');
    }
  }

  // Assigned rather than declared as static class fields: the theme's own scripts use
  // static methods only, so this keeps the browser baseline unchanged. These are shared
  // by every instance on purpose - window.instgrm is one global, and only one modal may
  // be open per page.
  CreatorVideos.EMBED_SRC = 'https://www.instagram.com/embed.js';
  CreatorVideos.HOSTS = ['www.instagram.com', 'instagram.com'];
  CreatorVideos.PATH = /^\/(p|reel|tv)\/([A-Za-z0-9_-]+)\/?$/;
  CreatorVideos.TIMEOUT = 12000;
  CreatorVideos.SHEET_ANIM = 140;
  CreatorVideos.MOBILE_QUERY = '(max-width: 599.98px)';
  CreatorVideos.ZERO_TIME = '0:00';
  CreatorVideos.LOADING_TEXT = 'Loading Instagram post…';
  CreatorVideos.ERROR_TEXT = 'Unable to load this Instagram post.';
  CreatorVideos.embedPromise = null;
  CreatorVideos.activeInstance = null;

  customElements.define('creator-videos', CreatorVideos);
}
